import React, { useMemo, useRef, useState } from "react";
import {
  X,
  Cpu,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  CheckCircle,
  XCircle,
  Loader,
  AlertTriangle,
  Download,
  FileText,
  FileJson,
  FileSpreadsheet,
  FileDown,
  ExternalLink,
  Calendar,
} from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import jsPDF from "jspdf";

interface ExecutionTokenAnalyticsProps {
  executionData: any;
  workflowName?: string;
  onClose: () => void;
}

const ExecutionTokenAnalytics: React.FC<ExecutionTokenAnalyticsProps> = ({
  executionData,
  workflowName,
  onClose,
}) => {
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportRef = useRef<HTMLDivElement>(null);

  // Close export menu on outside click
  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (exportRef.current && !exportRef.current.contains(e.target as Node)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const tokenUsages = executionData?.token_usages || [];
  const overallProcessing = executionData?.overall_proccessing || {};
  const status = executionData?.overall_proccessing_status || "UNKNOWN";
  const totals = useMemo(() => {
    return tokenUsages.reduce(
      (acc: any, u: any) => ({
        prompt_tokens: acc.prompt_tokens + (u.prompt_tokens || 0),
        completion_tokens: acc.completion_tokens + (u.completion_tokens || 0),
        total_tokens: acc.total_tokens + (u.total_tokens || 0),
      }),
      { prompt_tokens: 0, completion_tokens: 0, total_tokens: 0 }
    );
  }, [tokenUsages]);

  const promptVsCompletion = useMemo(() => {
    if (!totals.total_tokens) return [];
    return [
      { name: "Prompt Tokens", value: totals.prompt_tokens, color: "#2563eb" },
      {
        name: "Completion Tokens",
        value: totals.completion_tokens,
        color: "#0d9488",
      },
    ];
  }, [totals]);

  const nodeBarData = useMemo(() => {
    return tokenUsages.map((u: any) => ({
      name:
        u.node_name?.length > 18
          ? u.node_name.substring(0, 18) + "..."
          : u.node_name,
      fullName: u.node_name,
      prompt: u.prompt_tokens || 0,
      completion: u.completion_tokens || 0,
      total: u.total_tokens || 0,
    }));
  }, [tokenUsages]);

  const formatTokenCount = (count: number) => {
    if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(2)}M`;
    if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
    return count.toLocaleString("en-IN");
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat("en-IN", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(date);
  };

  const getStatusIcon = (s: string) => {
    switch (s) {
      case "DONE":
        return <CheckCircle className="w-4 h-4 text-emerald-500" />;
      case "FAILED":
        return <XCircle className="w-4 h-4 text-rose-500" />;
      case "IN_PROGRESS":
        return <Loader className="w-4 h-4 text-blue-500 animate-spin" />;
      default:
        return <AlertTriangle className="w-4 h-4 text-amber-500" />;
    }
  };

  const getStatusColor = (s: string) => {
    switch (s) {
      case "DONE":
        return "bg-emerald-50 text-emerald-700 border-emerald-200";
      case "FAILED":
        return "bg-rose-50 text-rose-700 border-rose-200";
      case "IN_PROGRESS":
        return "bg-blue-50 text-blue-700 border-blue-200";
      default:
        return "bg-amber-50 text-amber-700 border-amber-200";
    }
  };

  // Export functions
  const buildReportData = () => {
    return {
      execution_id: executionData?._id || executionData?.id,
      workflow_name: workflowName || "Workflow",
      status,
      duration: overallProcessing.time_taken
        ? `${overallProcessing.time_taken.toFixed(2)}s`
        : "-",
      started_at: overallProcessing.start_time || executionData?.createdAt,
      finished_at: overallProcessing.end_time || null,
      total_tokens: totals.total_tokens,
      prompt_tokens: totals.prompt_tokens,
      completion_tokens: totals.completion_tokens,
      token_usages: tokenUsages,
    };
  };

  const exportCSV = () => {
    const data = buildReportData();
    const lines = [
      `Execution Token Report - ${data.workflow_name}`,
      `Execution ID,${data.execution_id}`,
      `Status,${data.status}`,
      `Duration,${data.duration}`,
      `Started At,${data.started_at || "-"}`,
      `Finished At,${data.finished_at || "-"}`,
      "",
      "Token Summary",
      `Total Tokens,${data.total_tokens}`,
      `Prompt Tokens,${data.prompt_tokens}`,
      `Completion Tokens,${data.completion_tokens}`,
    ];
    downloadFile(lines.join("\n"), "execution-token-report.csv", "text/csv");
  };

  const exportJSON = () => {
    const { token_usages, ...restData } = buildReportData();
    downloadFile(
      JSON.stringify(restData, null, 2),
      "execution-token-report.json",
      "application/json"
    );
  };

  const exportText = () => {
    const data = buildReportData();
    const lines = [
      `EXECUTION TOKEN REPORT`,
      `${"=".repeat(50)}`,
      `Workflow: ${data.workflow_name}`,
      `Execution ID: ${data.execution_id}`,
      `Status: ${data.status}`,
      `Duration: ${data.duration}`,
      `Started: ${data.started_at ? formatDate(data.started_at) : "-"}`,
      `Finished: ${data.finished_at ? formatDate(data.finished_at) : "-"}`,
      "",
      `TOKEN SUMMARY`,
      `${"-".repeat(30)}`,
      `Total Tokens:      ${formatTokenCount(data.total_tokens)}`,
      `Prompt Tokens:     ${formatTokenCount(data.prompt_tokens)}`,
      `Completion Tokens: ${formatTokenCount(data.completion_tokens)}`,
    ];
    downloadFile(
      lines.join("\n"),
      "execution-token-report.txt",
      "text/plain"
    );
  };

  const exportPDF = () => {
    const data = buildReportData();
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 15;

    // Header bar
    doc.setFillColor(37, 99, 235);
    doc.rect(0, 0, pageWidth, 28, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Execution Token Report", 14, 12);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(data.workflow_name, 14, 20);
    doc.text(`ID: ${data.execution_id}`, 14, 26);

    y = 38;
    doc.setTextColor(0, 0, 0);

    // Summary cards row
    const cardWidth = (pageWidth - 28 - 12) / 3;
    const cards = [
      { label: "Status", value: data.status },
      { label: "Duration", value: data.duration },
      { label: "Total Tokens", value: formatTokenCount(data.total_tokens) },
    ];
    cards.forEach((card, i) => {
      const x = 14 + i * (cardWidth + 6);
      doc.setFillColor(248, 250, 252);
      doc.roundedRect(x, y, cardWidth, 18, 2, 2, "F");
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.text(card.label, x + 4, y + 7);
      doc.setFontSize(12);
      doc.setTextColor(15, 23, 42);
      doc.setFont("helvetica", "bold");
      doc.text(card.value, x + 4, y + 14);
      doc.setFont("helvetica", "normal");
    });

    y += 28;

    // Footer
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setTextColor(148, 163, 184);
      doc.text(
        `Page ${i} of ${pageCount}`,
        pageWidth - 30,
        doc.internal.pageSize.getHeight() - 8
      );
    }

    doc.save("execution-token-report.pdf");
  };

  const downloadFile = (content: string, filename: string, type: string) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-[9999] p-6 flex items-stretch justify-stretch">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full h-full bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-50 border border-blue-200 flex items-center justify-center">
              <Cpu className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {workflowName || "Workflow"}
              </h2>
              <p className="text-xs text-gray-500">
                Execution level token usage analytics
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="https://www.llm-prices.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              LLM Price Calculator
            </a>
            {/* Export Button */}
            <div className="relative" ref={exportRef}>
              <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Download className="w-4 h-4" />
                Export
              </button>
              {showExportMenu && (
                <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1">
                  <button
                    onClick={() => {
                      exportCSV();
                      setShowExportMenu(false);
                    }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <FileSpreadsheet className="w-4 h-4 text-emerald-500" />
                    Export CSV
                  </button>
                  <button
                    onClick={() => {
                      exportJSON();
                      setShowExportMenu(false);
                    }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <FileJson className="w-4 h-4 text-blue-500" />
                    Export JSON
                  </button>
                  <button
                    onClick={() => {
                      exportText();
                      setShowExportMenu(false);
                    }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <FileText className="w-4 h-4 text-gray-500" />
                    Export Text
                  </button>
                  <button
                    onClick={() => {
                      exportPDF();
                      setShowExportMenu(false);
                    }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <FileDown className="w-4 h-4 text-rose-500" />
                    Export PDF
                  </button>
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Applied Filter Preview */}
        <div className="px-6 py-3 border-b border-gray-100 bg-slate-50/50 flex-shrink-0">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-full">
              <Calendar className="h-3 w-3" />
              {formatDate(overallProcessing.start_time || executionData?.createdAt)}
            </span>
            <span className="text-gray-400">|</span>
            <span>
              Status: <strong className={status === "DONE" ? "text-emerald-600" : status === "FAILED" ? "text-rose-600" : "text-blue-600"}>{status}</strong>
              {" "}&middot;{" "}
              Duration: <strong>{overallProcessing.time_taken != null ? `${overallProcessing.time_taken.toFixed(2)}s` : "-"}</strong>
              {" "}&middot;{" "}
              {totals.total_tokens.toLocaleString("en-IN")} total tokens
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {/* Status */}
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="text-xs font-medium text-gray-500 mb-2">
                Status
              </div>
              <div className="flex items-center gap-2">
                {getStatusIcon(status)}
                <span
                  className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${getStatusColor(status)}`}
                >
                  {status}
                </span>
              </div>
            </div>

            {/* Duration */}
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="text-xs font-medium text-gray-500 mb-2">
                Duration
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-500" />
                <span className="text-lg font-bold text-gray-900">
                  {overallProcessing.time_taken != null
                    ? `${overallProcessing.time_taken.toFixed(2)}s`
                    : "-"}
                </span>
              </div>
            </div>

            {/* Total Tokens */}
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="text-xs font-medium text-gray-500 mb-2">
                Total Tokens
              </div>
              <div className="flex items-center gap-2">
                <Cpu className="w-4 h-4 text-blue-600" />
                <span className="text-lg font-bold text-gray-900">
                  {formatTokenCount(totals.total_tokens)}
                </span>
              </div>
            </div>

            {/* Prompt Tokens */}
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="text-xs font-medium text-gray-500 mb-2">
                Prompt Tokens
              </div>
              <div className="flex items-center gap-2">
                <ArrowUpRight className="w-4 h-4 text-blue-500" />
                <span className="text-lg font-bold text-gray-900">
                  {formatTokenCount(totals.prompt_tokens)}
                </span>
              </div>
            </div>

            {/* Completion Tokens */}
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="text-xs font-medium text-gray-500 mb-2">
                Completion Tokens
              </div>
              <div className="flex items-center gap-2">
                <ArrowDownRight className="w-4 h-4 text-teal-500" />
                <span className="text-lg font-bold text-gray-900">
                  {formatTokenCount(totals.completion_tokens)}
                </span>
              </div>
            </div>
          </div>

          {/* Timing Details */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-gray-800 mb-3">
              Timing Details
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Started At</span>
                <p className="font-medium text-gray-900 mt-0.5">
                  {formatDate(
                    overallProcessing.start_time || executionData?.createdAt
                  )}
                </p>
              </div>
              <div>
                <span className="text-gray-500">Finished At</span>
                <p className="font-medium text-gray-900 mt-0.5">
                  {overallProcessing.end_time
                    ? formatDate(overallProcessing.end_time)
                    : "-"}
                </p>
              </div>
              <div>
                <span className="text-gray-500">Total Duration</span>
                <p className="font-medium text-gray-900 mt-0.5">
                  {overallProcessing.time_taken != null
                    ? `${overallProcessing.time_taken.toFixed(2)} seconds`
                    : "-"}
                </p>
              </div>
            </div>
          </div>

          {/* Charts Row */}
          {tokenUsages.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Prompt vs Completion Donut */}
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-gray-800 mb-4">
                  Prompt vs Completion
                </h3>
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie
                      data={promptVsCompletion}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={85}
                      dataKey="value"
                      paddingAngle={3}
                      stroke="none"
                    >
                      {promptVsCompletion.map((entry, idx) => (
                        <Cell key={idx} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value: number) => [
                        formatTokenCount(value),
                        "Tokens",
                      ]}
                    />
                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      iconType="circle"
                      iconSize={8}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Token Usage by Node Bar Chart */}
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-gray-800 mb-4">
                  Token Usage by Node
                </h3>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={nodeBarData} layout="vertical">
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#e2e8f0"
                      horizontal={false}
                    />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis
                      dataKey="name"
                      type="category"
                      width={140}
                      tick={{ fontSize: 11 }}
                    />
                    <Tooltip
                      formatter={(value: number, name: string) => [
                        formatTokenCount(value),
                        name.charAt(0).toUpperCase() + name.slice(1),
                      ]}
                    />
                    <Bar
                      dataKey="prompt"
                      fill="#2563eb"
                      stackId="tokens"
                      radius={[0, 0, 0, 0]}
                      name="Prompt"
                    />
                    <Bar
                      dataKey="completion"
                      fill="#0d9488"
                      stackId="tokens"
                      radius={[0, 4, 4, 0]}
                      name="Completion"
                    />
                    <Legend iconType="circle" iconSize={8} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Token Usage Table */}
          {tokenUsages.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-800">
                  Node Token Breakdown
                </h3>
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                  {tokenUsages.length}{" "}
                  {tokenUsages.length === 1 ? "node" : "nodes"}
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-left">
                      <th className="px-4 py-2.5 font-semibold text-gray-600 rounded-tl-lg">
                        Node Name
                      </th>
                      <th className="px-4 py-2.5 font-semibold text-gray-600 text-right">
                        Prompt
                      </th>
                      <th className="px-4 py-2.5 font-semibold text-gray-600 text-right">
                        Completion
                      </th>
                      <th className="px-4 py-2.5 font-semibold text-gray-600 text-right rounded-tr-lg">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {tokenUsages.map((u: any, idx: number) => (
                      <tr
                        key={idx}
                        className={`border-b border-gray-100 ${idx % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}
                      >
                        <td className="px-4 py-2.5 font-medium text-gray-800">
                          {u.node_name}
                        </td>
                        <td className="px-4 py-2.5 text-right text-gray-600 font-mono text-xs">
                          {formatTokenCount(u.prompt_tokens || 0)}
                        </td>
                        <td className="px-4 py-2.5 text-right text-gray-600 font-mono text-xs">
                          {formatTokenCount(u.completion_tokens || 0)}
                        </td>
                        <td className="px-4 py-2.5 text-right font-semibold text-gray-900 font-mono text-xs">
                          {formatTokenCount(u.total_tokens || 0)}
                        </td>
                      </tr>
                    ))}
                    {/* Totals row */}
                    <tr className="bg-blue-50 border-t-2 border-blue-200">
                      <td className="px-4 py-2.5 font-bold text-blue-800 rounded-bl-lg">
                        Total
                      </td>
                      <td className="px-4 py-2.5 text-right font-bold text-blue-800 font-mono text-xs">
                        {formatTokenCount(totals.prompt_tokens)}
                      </td>
                      <td className="px-4 py-2.5 text-right font-bold text-blue-800 font-mono text-xs">
                        {formatTokenCount(totals.completion_tokens)}
                      </td>
                      <td className="px-4 py-2.5 text-right font-bold text-blue-800 font-mono text-xs rounded-br-lg">
                        {formatTokenCount(totals.total_tokens)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Empty state */}
          {tokenUsages.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <Cpu className="w-12 h-12 mb-3 opacity-30" />
              <p className="text-sm font-medium">No token usage data</p>
              <p className="text-xs mt-1">
                This execution has no recorded token usage
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExecutionTokenAnalytics;
